import { i18n } from '@boot/i18n';
import { notifyError } from '@boot/popups';
import { useSharedStore } from '@stores/shared.js';
import pinia from '@stores/index.js';
import { createApp } from 'vue';
import App from '../App.vue';
import { useAuthStore } from '@stores/auth';
import { useFacilitiesStore } from '@stores/facilities.ts';
import { useAssetsStore } from '@stores/assets.ts';
import { useSettingsStore } from '@stores/settings.js';
import { isRouteAllowedForRoleWithModules } from '@router/roleAccess';

/**
 * Use the shared Pinia instance from stores/index.js
 * This ensures consistent state management across the application
 * For more information see:
 * https://pinia.vuejs.org/core-concepts/outside-component-usage.html
 */
const app = createApp(App);
app.use(pinia);

export default ({ router, store }) => {
    async function loadFacilitiesIfNeeded(to, next) {
        // before we load facilities, we check if we are not being redirected from a loading facilities error
        // so that we don't enter an infinite loop
        if (!(to.name === 'facility_list' && to.query.showReloadBanner)) {
            const facilitiesStore = useFacilitiesStore();
            const facilities = facilitiesStore.getFacilities;

            // if facilities is not null, then an attempt to load facilities was already previously made
            if (facilities === null) {
                try {
                    await facilitiesStore.loadFacilitiesAction();
                } catch (error) {
                    next({
                        name: 'facility_list',
                        query: {
                            showReloadBanner: true
                        }
                    });
                }
            }
        }
    }

    // log router errors
    router.onError(error => {
        console.error('Router error: ');
        console.error('Name: ', error.name);
        console.error('Type: ', error.type);
        console.error('Message: ', error.message);
        console.error('Request: ', error.request);
    });

    router.beforeEach(async (to, from, next) => {
        const isAuthenticated = await useAuthStore().isAuthenticatedAction();

        // initialize application on refresh
        if (isAuthenticated) {
            // --- ROLE-BASED ACCESS CONTROL ---
            // Check if route requires role-based access control (skip for 404 and other bypass routes)
            if (to.meta.bypassRoleCheck !== true) {
                const authStore = useAuthStore();
                const settingsStore = useSettingsStore();
                const userRole = authStore.getUserRole;
                const allowedRoles = to.meta.allowedRoles;
                const isLiveOEEEnabled = settingsStore.isLiveOEEModuleEnabled;
                const isPlanningEnabled = settingsStore.isPlanningModuleEnabled;

                // Check if user has permission to access this route
                // Takes module flags into account - when only Planning is enabled,
                // all roles inherit planner permissions
                if (
                    !isRouteAllowedForRoleWithModules(
                        to.name,
                        userRole,
                        allowedRoles,
                        isLiveOEEEnabled,
                        isPlanningEnabled
                    )
                ) {
                    console.warn(
                        `Access denied: User with role "${userRole}" attempted to access route "${to.name}"`
                    );

                    // Show error notification
                    notifyError(
                        i18n.global.t(
                            'You do not have permission to access this page.'
                        )
                    );

                    // Redirect to facility list
                    next({ name: 'facility_list' });
                    return;
                }

                // Special handling for planners accessing the planning route
                // Check if planning module is enabled
                if (userRole === 'planner' && to.name === 'planning') {
                    const settingsStore = useSettingsStore();
                    await settingsStore.loadSettingsAction();
                    const isPlanningEnabled =
                        settingsStore.isPlanningModuleEnabled;

                    if (!isPlanningEnabled) {
                        // Show error notification
                        notifyError(
                            i18n.global.t(
                                'Your account has a planner role but the planning module is not enabled. Please contact support.'
                            )
                        );

                        // Redirect to no modules enabled page
                        next({ name: 'facility_list' });
                        return;
                    }
                }
            }

            await loadFacilitiesIfNeeded(to, next);

            // load assets
            if ('facilityId' in to.params) {
                // select this facility
                const facilityId = to.params['facilityId'];
                const facilitiesStore = useFacilitiesStore();
                const assetsStore = useAssetsStore();

                facilitiesStore.selectFacilityAction(facilityId);

                // Load assets. Only proceed with making the request to load asset
                // if we are loading assets for this facility for the first time
                // in order to prevent infinite loop.
                const assets = assetsStore.getAssets(facilityId);
                const checkAssetsLoadingFailed =
                    facilitiesStore.checkAssetsLoadingFailed(facilityId);

                if (assets.length === 0 && !checkAssetsLoadingFailed) {
                    try {
                        await assetsStore.loadAssetsAction({
                            facilityId: facilityId
                        });
                    } catch (error) {
                        facilitiesStore.addLoadingAssetsFailedAction(
                            facilityId
                        );
                        next({
                            name: 'facility_dashboard',
                            params: {
                                facilityId: facilityId
                            }
                        });
                    }
                }
            }
        }

        // check permissions
        if (
            to.matched.some(
                // if isPublic not in record.meta, treat it as private
                record => !record.meta.isPublic || !('isPublic' in record.meta)
            )
        ) {
            // if route is private, check if user is authenticated
            if (isAuthenticated) {
                handleRedirect(to, next);
            } else {
                const hasValidRefreshToken =
                    await useAuthStore().hasValidRefreshTokenAction();
                if (!hasValidRefreshToken) {
                    // Set a notification popup to inform the user that their session has expired
                    useSharedStore().setNotification({
                        type: 'error',
                        message: i18n.global.t(
                            'Your session has expired. Please log in again to continue.'
                        )
                    });
                    next({
                        name: 'login'
                    });
                }

                // navigate them to the login page
                next({
                    name: 'login',
                    query: { redirect: to.fullPath }
                });
            }
        } else {
            // if route is login, check if user is already authenticated, if so navigate them to home page
            // to prevent another user from logging in and sharing session states
            if (isAuthenticated && to.name === 'login') {
                handleRedirect(to, next, 'facility_list');
            } else {
                next();
            }
        }
    });
};

/**
 * Handle redirect to the redirect page if it exists
 * @param {Object} to - the route object
 * @param {Function} next - the next function
 * @param {String} defaultPath - the default path to navigate to if redirect does not exist
 */
function handleRedirect(to, next, defaultPath) {
    const { redirect, ...query } = to.query;
    if (redirect) {
        // navigate to the redirect page if it exists
        // and pass on all extra query parameters
        next({ path: redirect, query: query });
    } else {
        // if no redirect page, navigate to the default page if it exists
        if (defaultPath) {
            next({ name: defaultPath, query: query });
        } else {
            next();
        }
    }
}
